import React, { useState } from 'react';
import {
  FileText, Plus, Eye, Check, X, AlertTriangle
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormat } from '../../i18n/format.js';
import { apiRequest } from '../../api/core.js';
import { isFlagOn } from '../../utils/dbFlags';

// Icônes constantes, libellés résolus au rendu par t().
const NOTICE_TYPES = {
  telematics: { icon: '📡' },
  location: { icon: '📍' },
  performance: { icon: '📊' },
  camera: { icon: '📷' },
  general: { icon: '📄' }
};

function PrivacyNoticesPanel({ onMessage }) {
  const { t, fmt } = useFormat();
  useQueryClient(); // Reserved for future mutations
  const [showForm, setShowForm] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [formData, setFormData] = useState({
    notice_type: 'telematics',
    title: '',
    summary: '',
    content: '',
    language: 'fr',
    version: '1.0',
    requires_acknowledgment: true
  });

  const { data: noticesData, isLoading } = useQuery({
    queryKey: ['privacy_notices'],
    queryFn: () => apiRequest('/privacy_notice')
  });

  const notices = noticesData?.data || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            {t('compliance.notices.title')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('compliance.notices.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('compliance.notices.newButton')}
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-800">{t('compliance.notices.info.title')}</h4>
            <p className="text-sm text-blue-700 mt-1">
              {t('compliance.notices.info.body')}
            </p>
          </div>
        </div>
      </div>

      {/* Notices by Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(NOTICE_TYPES).map(([type, config]) => {
          const typeNotices = notices.filter(n => n.notice_type === type);
          const activeNotice = typeNotices.find(n => isFlagOn(n.is_active));

          return (
            <div key={type} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <span className="text-2xl mr-2">{config.icon}</span>
                  <span className="font-medium">{t(`compliance.notices.types.${type}`)}</span>
                </div>
                {activeNotice ? (
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full flex items-center">
                    <Check className="w-3 h-3 mr-1" /> {t('compliance.notices.badge.active')}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full flex items-center">
                    <X className="w-3 h-3 mr-1" /> {t('compliance.notices.badge.missing')}
                  </span>
                )}
              </div>

              {activeNotice ? (
                <>
                  <h4 className="text-sm font-medium truncate">{activeNotice.title}</h4>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{activeNotice.summary}</p>
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
                    <span>v{activeNotice.version}</span>
                    <button
                      onClick={() => setSelectedNotice(activeNotice)}
                      className="text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <Eye className="w-3 h-3 mr-1" /> {t('compliance.notices.view')}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('compliance.notices.noneForType')}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* All Notices List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h4 className="font-semibold">{t('compliance.notices.listTitle')}</h4>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('common.states.loading')}</div>
        ) : notices.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{t('compliance.notices.empty.title')}</p>
            <p className="text-sm mt-1">{t('compliance.notices.empty.hint')}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('compliance.notices.table.type')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('compliance.notices.table.title')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.notices.table.version')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.notices.table.language')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.notices.table.acknowledgment')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.notices.table.status')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.notices.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {notices.map(notice => (
                <tr key={notice.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="flex items-center">
                      <span className="mr-2">{NOTICE_TYPES[notice.notice_type]?.icon || '📄'}</span>
                      {t(`compliance.notices.types.${notice.notice_type}`, { defaultValue: notice.notice_type })}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium">{notice.title}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">{notice.summary}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-mono text-sm">{notice.version}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                      {notice.language === 'fr' ? '🇫🇷' : notice.language === 'en' ? '🇬🇧' : '🇩🇪'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {notice.requires_acknowledgment ? (
                      <Check className="w-5 h-5 text-green-600 mx-auto" />
                    ) : (
                      <X className="w-5 h-5 text-gray-300 mx-auto" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {notice.is_active ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{t('compliance.notices.badge.active')}</span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">{t('compliance.notices.badge.inactive')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => setSelectedNotice(notice)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Required Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2 text-amber-600" />
          {t('compliance.notices.required.title')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <RequiredInfoItem
            label={t('compliance.notices.required.controller.label')}
            description={t('compliance.notices.required.controller.description')}
          />
          <RequiredInfoItem
            label={t('compliance.notices.required.dpo.label')}
            description={t('compliance.notices.required.dpo.description')}
          />
          <RequiredInfoItem
            label={t('compliance.notices.required.purposes.label')}
            description={t('compliance.notices.required.purposes.description')}
          />
          <RequiredInfoItem
            label={t('compliance.notices.required.legalBasis.label')}
            description={t('compliance.notices.required.legalBasis.description')}
          />
          <RequiredInfoItem
            label={t('compliance.notices.required.recipients.label')}
            description={t('compliance.notices.required.recipients.description')}
          />
          <RequiredInfoItem
            label={t('compliance.notices.required.retention.label')}
            description={t('compliance.notices.required.retention.description')}
          />
          <RequiredInfoItem
            label={t('compliance.notices.required.rights.label')}
            description={t('compliance.notices.required.rights.description')}
          />
          <RequiredInfoItem
            label={t('compliance.notices.required.complaint.label')}
            description={t('compliance.notices.required.complaint.description')}
          />
        </div>
      </div>

      {/* View Notice Modal */}
      {selectedNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">{selectedNotice.title}</h3>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <span>{t(`compliance.notices.types.${selectedNotice.notice_type}`, { defaultValue: selectedNotice.notice_type })}</span>
                  <span>•</span>
                  <span>v{selectedNotice.version}</span>
                </div>
              </div>
              <button onClick={() => setSelectedNotice(null)}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {selectedNotice.summary && (
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-800 mb-1">{t('compliance.notices.detail.summary')}</h4>
                  <p className="text-sm text-blue-700">{selectedNotice.summary}</p>
                </div>
              )}
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-sm bg-gray-50 p-4 rounded-lg">
                  {selectedNotice.content}
                </pre>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-between">
              <div className="text-sm text-gray-500">
                {selectedNotice.effective_date && (
                  <span>{t('compliance.notices.detail.effectiveDate', { date: fmt.date(selectedNotice.effective_date) })}</span>
                )}
              </div>
              <button
                onClick={() => setSelectedNotice(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                {t('common.actions.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{t('compliance.notices.form.title')}</h3>
              <button onClick={() => setShowForm(false)}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <form className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.notices.form.type')}</label>
                <select
                  value={formData.notice_type}
                  onChange={e => setFormData({ ...formData, notice_type: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {Object.entries(NOTICE_TYPES).map(([key, config]) => (
                    <option key={key} value={key}>{config.icon} {t(`compliance.notices.types.${key}`)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.notices.form.titleLabel')}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder={t('compliance.notices.form.titlePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.notices.form.summary')}</label>
                <textarea
                  value={formData.summary}
                  onChange={e => setFormData({ ...formData, summary: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  rows="2"
                  placeholder={t('compliance.notices.form.summaryPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.notices.form.content')}</label>
                <textarea
                  value={formData.content}
                  onChange={e => setFormData({ ...formData, content: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  rows="6"
                  placeholder={t('compliance.notices.form.contentPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.notices.form.language')}</label>
                  <select
                    value={formData.language}
                    onChange={e => setFormData({ ...formData, language: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="fr">{t('compliance.notices.form.languages.fr')}</option>
                    <option value="en">{t('compliance.notices.form.languages.en')}</option>
                    <option value="de">{t('compliance.notices.form.languages.de')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.notices.form.version')}</label>
                  <input
                    type="text"
                    value={formData.version}
                    onChange={e => setFormData({ ...formData, version: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="1.0"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.requires_acknowledgment}
                  onChange={e => setFormData({ ...formData, requires_acknowledgment: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm text-gray-700">
                  {t('compliance.notices.form.ackRequired')}
                </label>
              </div>

              <div className="border-t pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('common.actions.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function RequiredInfoItem({ label, description }) {
  return (
    <div className="flex items-start">
      <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
      <div>
        <span className="font-medium">{label}</span>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  );
}

export default PrivacyNoticesPanel;
